import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, FileText, Receipt, Loader2, X, ChevronRight, Calendar, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { estimatesApi, invoicesApi, customersApi, jobsApi } from "../../lib/api";
import { DocDetail, DocStatusPills, estimateStatusLabel } from "../Estimates";

const EST_STATUSES = ["Draft", "Sent", "Accepted", "Declined", "Converted", "Expired"];
const INV_STATUSES = ["Unpaid", "Partially Paid", "Paid", "Overdue", "Voided"];

function money(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Mobile Estimates & Invoices — stacked-card list (no horizontal scroll)
 * with a tab switch, search, status chips, and a full-screen detail sheet
 * driven by `?id=` (line items, totals, status change, record payment).
 */
export default function MobileEstimates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("estimates");
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const activeId = searchParams.get("id") || null;

  const load = useCallback(async () => {
    try {
      const [e, i, c, j] = await Promise.all([
        estimatesApi.list(),
        invoicesApi.list(),
        customersApi.list(),
        jobsApi.list(),
      ]);
      setEstimates(e || []);
      setInvoices(i || []);
      setCustomers(c || []);
      setJobs(j || []);
    } catch {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusOptions = tab === "estimates" ? EST_STATUSES : INV_STATUSES;

  const filtered = useMemo(() => {
    const src = tab === "estimates" ? estimates : invoices;
    const q = search.trim().toLowerCase();
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
    return src
      .filter((d) => (statusFilter === "All" ? true : d.status === statusFilter))
      .filter((d) => !q || `${d.id} ${d.customer} ${d.title || ""} ${estimateStatusLabel(d.status)}`.toLowerCase().includes(q))
      .filter((d) => {
        if (!fromMs && !toMs) return true;
        const t = d.issuedAt ? new Date(d.issuedAt).getTime() : 0;
        if (!t) return false;
        if (fromMs && t < fromMs) return false;
        if (toMs && t > toMs) return false;
        return true;
      })
      .sort((a, b) => new Date(b.issuedAt || 0).getTime() - new Date(a.issuedAt || 0).getTime());
  }, [tab, estimates, invoices, search, statusFilter, dateFrom, dateTo]);

  const openDoc = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("id", id);
    setSearchParams(next);
  };
  const closeDoc = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next);
  };

  if (activeId) {
    const est = estimates.find((e) => e.id === activeId);
    const inv = !est ? invoices.find((i) => i.id === activeId) : null;
    const found = est || inv;
    const kind = est ? "estimate" : "invoice";
    if (!found) {
      return (
        <div className="mv md:hidden fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3" style={{ backgroundColor: "#F1F5F9" }}>
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      );
    }
    return (
      <DocDetail
        kind={kind}
        doc={found}
        customers={customers}
        jobs={jobs}
        mobileLayout
        mobileBackLabel={tab === "estimates" ? "Estimates" : "Invoices"}
        onClose={() => {
          closeDoc();
          load().catch(() => {});
        }}
        onUpdated={(updated) => {
          if (kind === "estimate") setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
          else setInvoices((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
        }}
        onDeleted={(id) => {
          if (kind === "estimate") setEstimates((prev) => prev.filter((e) => e.id !== id));
          else setInvoices((prev) => prev.filter((i) => i.id !== id));
          closeDoc();
        }}
        onConverted={(newInvoice) => {
          setTab("invoices");
          load().catch(() => {});
          if (newInvoice?.id) {
            openDoc(newInvoice.id);
            toast.success(`Invoice ${newInvoice.id} ready — collect payment`);
          } else {
            closeDoc();
            toast.success("Converted to invoice — see the Invoices tab");
          }
        }}
        onOpenCustomer={(cid) => cid && navigate(`/customers?id=${cid}`)}
      />
    );
  }

  return (
    <div
      className="mv md:hidden min-h-screen w-full"
      style={{
        color: "#0f172a",
        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-estimates"
    >
      {/* Tab switch */}
      <div className="px-4 pt-3">
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1" data-testid="mobile-billing-tabs">
          <button
            onClick={() => { setTab("estimates"); setStatusFilter("All"); }}
            data-testid="mobile-tab-estimates"
            className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 h-9 rounded-md text-[13.5px] font-semibold ${tab === "estimates" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}
          >
            <FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Estimates</span>
            <span className={`text-[10.5px] font-bold px-1.5 rounded-full shrink-0 ${tab === "estimates" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>{estimates.length}</span>
          </button>
          <button
            onClick={() => { setTab("invoices"); setStatusFilter("All"); }}
            data-testid="mobile-tab-invoices"
            className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 h-9 rounded-md text-[13.5px] font-semibold ${tab === "invoices" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}
          >
            <Receipt className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Invoices</span>
            <span className={`text-[10.5px] font-bold px-1.5 rounded-full shrink-0 ${tab === "invoices" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>{invoices.length}</span>
          </button>
        </div>
      </div>

      {/* Date range filter — clearly visible at the top of the page */}
      <div className="px-4 pt-3">
        <div className="rounded-2xl bg-white border border-slate-200 p-3 flex items-center gap-2" data-testid="mobile-billing-date-filter">
          <div className="flex-1">
            <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">From date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              data-testid="mobile-billing-date-from"
              className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          <div className="flex-1">
            <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">To date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              data-testid="mobile-billing-date-to"
              className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              data-testid="mobile-billing-date-clear"
              className="self-end h-9 px-3 rounded-md text-[11.5px] font-semibold text-blue-600 hover:bg-blue-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${tab}…`}
            data-testid="mobile-billing-search"
            className="w-full h-11 pl-9 pr-9 rounded-full border border-slate-200 text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            style={{ backgroundColor: "#F1F5F9", color: "#0f172a" }}
          />
          {search && (
            <button onClick={() => setSearch("")} data-testid="mobile-billing-clear-search" className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-slate-400 active:bg-slate-100" aria-label="Clear search">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Status chips */}
      <div className="px-4 pt-2.5 flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="mobile-billing-status-chips">
        <button
          onClick={() => setStatusFilter("All")}
          data-testid="mobile-billing-chip-all"
          className={`px-3 h-8 rounded-full text-[12px] font-semibold whitespace-nowrap shrink-0 ${statusFilter === "All" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}
        >
          All
        </button>
        {statusOptions.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`mobile-billing-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-3 h-8 rounded-full text-[12px] font-semibold whitespace-nowrap shrink-0 ${statusFilter === s ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}
          >
            {estimateStatusLabel(s)}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-400 text-sm" data-testid="mobile-billing-loading">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading {tab}…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm" data-testid="mobile-billing-empty">
          {dateFrom || dateTo ? `No ${tab} in this date range.` : `No ${tab} found.`}
        </div>
      ) : (
        <div className="px-4 pt-3 space-y-2.5" data-testid="mobile-billing-list">
          {filtered.map((d) => (
            <DocCard key={d.id} doc={d} kind={tab === "estimates" ? "estimate" : "invoice"} onOpen={() => openDoc(d.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DocCard({ doc, kind, onOpen }) {
  const isInvoice = kind === "invoice";
  const showBalance = isInvoice && Number(doc.balanceDue) > 0 && doc.status !== "Voided";
  return (
    <button
      onClick={onOpen}
      data-testid={`mobile-billing-card-${doc.id}`}
      className="w-full text-left rounded-2xl bg-white border border-slate-200 p-3.5 active:bg-slate-50"
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="font-bold text-[15px] text-slate-900 truncate">{doc.id}</span>
        <div className="shrink-0"><DocStatusPills doc={doc} kind={kind} size="sm" /></div>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 min-w-0">
        <UserIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="text-[14px] font-semibold text-slate-800 truncate">{doc.customer || "Unnamed customer"}</span>
      </div>
      <div className="mt-1 text-[14px] text-slate-500 truncate">
        {doc.title || <span className="italic text-slate-400">(no title)</span>}
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 min-w-0">
        <span className="text-[13px] text-slate-400 shrink-0">{fmtDate(doc.issuedAt)}</span>
        <div className="text-right min-w-0">
          <div className="text-[16px] font-bold text-slate-900 truncate">{money(doc.total)}</div>
          {showBalance && <div className="text-[12px] font-semibold text-amber-700 truncate">Balance {money(doc.balanceDue)}</div>}
        </div>
      </div>
    </button>
  );
}

